import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Table, Spinner, Row, Col } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { getBackendUrl } from '../config';
import './Settings.scss';

/**
 * AdminNutzer ermöglicht Administratoren die direkte Verwaltung von Benutzern.
 * Dozenten können gesucht und aufgelistet werden, während Schüler und Admins
 * direkt per Eingabe ihrer ID verwaltet werden können.
 */
export default function AdminNutzer() {
  const { token } = useAuth();
  
  const [dozenten, setDozenten] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [editUserId, setEditUserId] = useState(null);
  const [editNewPassword, setEditNewPassword] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const [directUserId, setDirectUserId] = useState('');
  const [directPassword, setDirectPassword] = useState('');
  const [directDeleteConfirm, setDirectDeleteConfirm] = useState(false);

  const [directUser, setDirectUser] = useState(null);
  const [directUserLoading, setDirectUserLoading] = useState(false);
  const [directUserError, setDirectUserError] = useState('');

  useEffect(() => {
    if (!directUserId || parseInt(directUserId, 10) <= 0) {
      setDirectUser(null);
      setDirectUserError('');
      return;
    }

    const fetchDirectUser = async () => {
      setDirectUserLoading(true);
      setDirectUserError('');
      setDirectUser(null);
      try {
        const response = await fetch(`${getBackendUrl()}/chat/${parseInt(directUserId, 10)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) {
          if (response.status === 404) {
            setDirectUserError('Kein aktiver Benutzer mit dieser ID gefunden');
          } else {
            setDirectUserError('Fehler beim Abrufen des Benutzers');
          }
          return;
        }
        const data = await response.json();
        if (data && data.andererNutzer) {
          // If the user's role is 'deleted', treat them as not found
          if (data.andererNutzer.rolle === 'deleted') {
            setDirectUserError('Benutzer ist deaktiviert (soft-deleted)');
          } else {
            setDirectUser(data.andererNutzer);
          }
        } else {
          setDirectUserError('Kein aktiver Benutzer mit dieser ID gefunden');
        }
      } catch (err) {
        setDirectUserError('Fehler beim Verbinden mit dem Server');
      } finally {
        setDirectUserLoading(false);
      }
    };

    const delayDebounceFn = setTimeout(() => {
      fetchDirectUser();
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [directUserId, token]);

  /**
   * Hilfsfunktion zur Ermittlung detaillierter API-Fehlermeldungen.
   */
  const handleResponseError = async (response, defaultMessage) => {
    let errorMsg = defaultMessage;
    try {
      const errData = await response.json();
      errorMsg = errData.message || errData.error || errorMsg;
    } catch (e) {
      if (response.statusText) {
        errorMsg = `${defaultMessage} (${response.status} - ${response.statusText})`;
      } else {
        errorMsg = `${defaultMessage} (Status-Code: ${response.status})`;
      }
    }
    return new Error(errorMsg);
  };

  /**
   * Sucht Dozenten anhand eines Suchbegriffs über die API.
   */
  const searchDozenten = async (query = '') => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${getBackendUrl()}/suche/dozent/${encodeURIComponent(query || '@')}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        throw await handleResponseError(response, 'Dozenten konnten nicht geladen werden.');
      }
      const data = await response.json();
      setDozenten(data.sort((a, b) => a.id - b.id));
    } catch (err) {
      setError(err.message || 'Fehler beim Suchen der Dozenten.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    searchDozenten('');
  }, [token]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    searchDozenten(searchTerm);
  };

  /**
   * Ändert das Passwort eines Benutzers (Dozent, Schüler oder Admin).
   */
  const handlePasswordChange = async (id, newPassword, isDirect = false) => {
    if (!newPassword.trim()) return;
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      const response = await fetch(`${getBackendUrl()}/user/${id}/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ newPassword: newPassword.trim() })
      });

      if (!response.ok) {
        throw await handleResponseError(response, 'Fehler beim Ändern des Passworts.');
      }

      setSuccess(`Passwort für Benutzer-ID #${id} erfolgreich geändert.`);
      if (isDirect) {
        setDirectPassword('');
        setDirectUserId('');
      } else {
        setEditUserId(null);
        setEditNewPassword('');
      }
    } catch (err) {
      setError(err.message || 'Passwortänderung fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Deaktiviert einen Benutzer (Soft-Delete) im Backend.
   */
  const handleDeleteUser = async (id, isDirect = false) => {
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      const response = await fetch(`${getBackendUrl()}/user/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw await handleResponseError(response, 'Fehler beim Löschen des Benutzers.');
      }

      setSuccess(`Benutzer mit ID #${id} wurde erfolgreich deaktiviert (Soft-Delete).`);
      if (isDirect) {
        setDirectUserId('');
        setDirectDeleteConfirm(false);
      } else {
        setDeleteConfirmId(null);
        searchDozenten(searchTerm);
      }
    } catch (err) {
      setError(err.message || 'Löschvorgang fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (user) => {
    setEditUserId(user.id);
    setEditNewPassword('');
    setDeleteConfirmId(null);
  };

  const cancelEdit = () => {
    setEditUserId(null);
    setEditNewPassword('');
  };

  return (
    <div className="vintage-settings p-2 p-sm-3 p-md-4" style={{ backgroundColor: '#fcfaf8', minHeight: '100vh', borderRadius: '12px' }}>
      <h1 className="page-header" style={{ color: '#2c3e50', fontWeight: '700' }}>Nutzer verwalten</h1>
      
      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Card className="settings-card shadow-sm border-0 mb-4">
        <Card.Header className="bg-white border-bottom">
          <span style={{ fontWeight: '600' }}>Direktverwaltung per ID (Für Schüler & Admins)</span>
        </Card.Header>
        <Card.Body>
          <p className="text-muted small mb-3">
            Da Schüler und Admins nicht aufgelistet werden können, können Sie diese hier direkt über ihre bekannte ID verwalten.
          </p>
          <Row className="align-items-end">
            <Col md={2} className="mb-3 mb-md-0">
              <Form.Label className="form-label mb-1">Benutzer-ID</Form.Label>
              <Form.Control
                type="number"
                placeholder="z. B. 3"
                value={directUserId}
                onChange={(e) => setDirectUserId(e.target.value)}
                disabled={submitting}
                className="form-control py-1 px-2"
                size="sm"
                style={{ height: '38px', borderRadius: '8px' }}
              />
            </Col>
            <Col md={4} className="mb-3 mb-md-0">
              <Form.Label className="form-label mb-1">Neues Passwort</Form.Label>
              <Form.Control
                type="password"
                placeholder="Neues Passwort eingeben"
                value={directPassword}
                onChange={(e) => setDirectPassword(e.target.value)}
                disabled={submitting || !directUserId}
                className="form-control py-1 px-2"
                size="sm"
                style={{ height: '38px', borderRadius: '8px' }}
              />
            </Col>
            <Col md={3} className="mb-3 mb-md-0">
              <Button 
                onClick={() => handlePasswordChange(directUserId, directPassword, true)}
                disabled={submitting || !directUserId || !directPassword.trim()}
                className="w-100"
                style={{ backgroundColor: '#b95c50', borderColor: '#b95c50', borderRadius: '8px', fontSize: '0.85rem', fontWeight: '700', height: '38px' }}
                size="sm"
              >
                Passwort ändern
              </Button>
            </Col>
            <Col md={3}>
              {directDeleteConfirm ? (
                <div className="d-flex gap-1 align-items-center justify-content-between p-1 border border-danger rounded bg-light" style={{ height: '38px' }}>
                  <span className="text-danger fw-bold ms-2" style={{ fontSize: '0.75rem' }}>Sicher?</span>
                  <div className="d-flex gap-1 me-1">
                    <Button 
                      variant="danger" 
                      size="sm" 
                      onClick={() => handleDeleteUser(directUserId, true)}
                      disabled={submitting}
                      style={{ fontSize: '0.75rem', padding: '2px 10px', borderRadius: '4px' }}
                    >
                      Ja, löschen
                    </Button>
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      onClick={() => setDirectDeleteConfirm(false)}
                      disabled={submitting}
                      style={{ fontSize: '0.75rem', padding: '2px 10px', borderRadius: '4px' }}
                    >
                      Nein
                    </Button>
                  </div>
                </div>
              ) : (
                <Button 
                  variant="outline-danger"
                  onClick={() => setDirectDeleteConfirm(true)}
                  disabled={submitting || !directUserId}
                  className="w-100"
                  style={{ borderRadius: '8px', fontSize: '0.85rem', fontWeight: '700', height: '38px' }}
                  size="sm"
                >
                  Nutzer löschen
                </Button>
              )}
            </Col>
          </Row>

          {/* Echtzeit-Nutzer-Vorschau */}
          {(directUserLoading || directUser || directUserError) && (
            <div className="mt-4 p-3 border rounded" style={{ backgroundColor: '#fdfbf9', borderColor: '#eae5e0', borderRadius: '12px' }}>
              {directUserLoading && (
                <div className="d-flex align-items-center text-muted">
                  <Spinner animation="border" size="sm" className="me-2" style={{ color: '#b95c50' }} />
                  <span>Suche Benutzer-Details...</span>
                </div>
              )}
              {directUserError && (
                <div className="text-danger small fw-semibold">
                  <i className="bi bi-exclamation-circle me-1"></i> {directUserError}
                </div>
              )}
              {directUser && (
                <div className="d-flex align-items-center flex-wrap gap-3">
                  <div className="d-flex align-items-center justify-content-center bg-primary text-white rounded-circle" style={{ width: '40px', height: '40px', fontWeight: 'bold' }}>
                    {directUser.vollerName?.[0] || '?' }
                  </div>
                  <div>
                    <div className="fw-bold text-dark" style={{ fontSize: '0.95rem' }}>
                      {directUser.vollerName} <span className="badge ms-2" style={{ backgroundColor: '#b95c50', fontSize: '0.75rem' }}>{directUser.rolle}</span>
                    </div>
                    <div className="text-muted small">
                      <i className="bi bi-envelope me-1"></i> {directUser.email}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="settings-card shadow-sm border-0">
        <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2 py-3">
          <span style={{ fontWeight: '600', fontSize: '1.1rem' }}>Dozentendatenbank ({dozenten.length})</span>
          <Form onSubmit={handleSearchSubmit} className="d-flex gap-2" style={{ maxWidth: '350px', width: '100%' }}>
            <Form.Control
              type="text"
              placeholder="Dozenten nach Name oder E-Mail suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="sm"
              style={{ borderRadius: '8px' }}
              className="form-control"
            />
            <Button type="submit" size="sm" style={{ backgroundColor: '#b95c50', borderColor: '#b95c50', borderRadius: '8px', padding: '6px 16px' }}>
              Suchen
            </Button>
          </Form>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" size="md" className="mb-2" style={{ color: '#b95c50' }} />
              <div className="text-muted">Dozenten werden gesucht...</div>
            </div>
          ) : dozenten.length === 0 ? (
            <div className="text-center py-5 text-muted">
              Keine Dozenten gefunden.
            </div>
          ) : (
            <>
              {/* Desktop-Ansicht: Tabelle */}
              <div className="table-responsive d-none d-md-block">
                <Table hover className="align-middle mb-0 text-nowrap" style={{ backgroundColor: 'transparent' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#fdfaf7', borderBottom: '1px solid #eae5e0' }}>
                      <th style={{ padding: '1.25rem 1.5rem', width: '80px', color: '#7f8c8d', fontSize: '0.85rem' }}>ID</th>
                      <th style={{ padding: '1.25rem 1.5rem', color: '#7f8c8d', fontSize: '0.85rem' }}>Name</th>
                      <th style={{ padding: '1.25rem 1.5rem', color: '#7f8c8d', fontSize: '0.85rem' }}>E-Mail</th>
                      <th style={{ padding: '1.25rem 1.5rem', width: '280px', color: '#7f8c8d', fontSize: '0.85rem', textAlign: 'right' }}>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dozenten.map(user => (
                      <tr key={user.id} style={{ borderBottom: '1px solid #eae5e0' }}>
                        <td style={{ padding: '1.25rem 1.5rem', fontWeight: '500' }}>#{user.id}</td>
                        <td style={{ padding: '1.25rem 1.5rem' }}>
                          <span style={{ fontSize: '1.05rem', color: '#2c3e50', fontWeight: '500' }}>{user.vollerName}</span>
                        </td>
                        <td style={{ padding: '1.25rem 1.5rem', color: '#7f8c8d' }}>{user.email}</td>
                        <td style={{ padding: '1.25rem 1.5rem', textAlign: 'right' }}>
                          {editUserId === user.id ? (
                            <div className="d-flex justify-content-end align-items-center gap-2">
                              <Form.Control
                                type="password"
                                placeholder="Neues PW"
                                value={editNewPassword}
                                onChange={(e) => setEditNewPassword(e.target.value)}
                                size="sm"
                                required
                                style={{ maxWidth: '150px', borderRadius: '8px' }}
                                className="form-control py-1"
                              />
                              <Button 
                                variant="success" 
                                size="sm" 
                                onClick={() => handlePasswordChange(user.id, editNewPassword)}
                                disabled={submitting || !editNewPassword.trim()}
                                style={{ borderRadius: '8px' }}
                              >
                                Speichern
                              </Button>
                              <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                onClick={cancelEdit}
                                disabled={submitting}
                                style={{ borderRadius: '8px' }}
                              >
                                Abbrechen
                              </Button>
                            </div>
                          ) : deleteConfirmId === user.id ? (
                            <div className="d-flex justify-content-end align-items-center gap-2">
                              <span className="text-danger" style={{ fontSize: '0.85rem', fontWeight: '600' }}>Sicher?</span>
                              <Button 
                                variant="danger" 
                                size="sm" 
                                onClick={() => handleDeleteUser(user.id)}
                                disabled={submitting}
                                style={{ borderRadius: '8px', padding: '6px 12px' }}
                              >
                                Ja, deaktivieren
                              </Button>
                              <Button 
                                variant="outline-secondary" 
                                size="sm" 
                                onClick={() => setDeleteConfirmId(null)}
                                disabled={submitting}
                                style={{ borderRadius: '8px', padding: '6px 12px' }}
                              >
                                Nein
                              </Button>
                            </div>
                          ) : (
                            <div className="d-flex justify-content-end gap-2">
                              <Button 
                                variant="outline-primary" 
                                size="sm" 
                                onClick={() => startEdit(user)}
                                disabled={submitting || editUserId !== null}
                                style={{ borderRadius: '8px', borderColor: '#b95c50', color: '#b95c50' }}
                                className="vintage-edit-btn"
                              >
                                PW ändern
                              </Button>
                              <Button 
                                variant="outline-danger" 
                                size="sm" 
                                onClick={() => { setDeleteConfirmId(user.id); cancelEdit(); }}
                                disabled={submitting || editUserId !== null}
                                style={{ borderRadius: '8px' }}
                              >
                                Löschen
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Mobile-Ansicht: Kachel-Kartenliste */}
              <div className="d-md-none p-3" style={{ backgroundColor: '#fdfbf9' }}>
                {dozenten.map(user => (
                  <div key={user.id} className="p-3 mb-3 border rounded bg-white shadow-sm d-flex flex-column gap-2" style={{ borderColor: '#eae5e0', borderRadius: '12px' }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted small" style={{ fontWeight: '600' }}>ID: #{user.id}</span>
                      <span className="text-muted small">{user.email}</span>
                    </div>
                    <div className="fw-bold fs-6 text-dark" style={{ fontFamily: "'Lora', serif" }}>
                      {user.vollerName}
                    </div>
                    <div className="d-flex flex-column gap-2 mt-2 pt-2 border-top" style={{ borderColor: '#eae5e0' }}>
                      {editUserId === user.id ? (
                        <div className="d-flex flex-column gap-2 w-100">
                          <Form.Control
                            type="password"
                            placeholder="Neues PW"
                            value={editNewPassword}
                            onChange={(e) => setEditNewPassword(e.target.value)}
                            size="sm"
                            required
                            className="form-control"
                            style={{ borderRadius: '8px' }}
                          />
                          <div className="d-flex gap-2">
                            <Button variant="success" size="sm" className="w-50" style={{ borderRadius: '8px' }} onClick={() => handlePasswordChange(user.id, editNewPassword)} disabled={submitting || !editNewPassword.trim()}>Speichern</Button>
                            <Button variant="outline-secondary" size="sm" className="w-50" style={{ borderRadius: '8px' }} onClick={cancelEdit} disabled={submitting}>Abbrechen</Button>
                          </div>
                        </div>
                      ) : deleteConfirmId === user.id ? (
                        <div className="d-flex gap-2 align-items-center justify-content-between w-100">
                          <span className="text-danger small fw-bold">Wirklich löschen?</span>
                          <div className="d-flex gap-2">
                            <Button variant="danger" size="sm" style={{ borderRadius: '8px', padding: '4px 12px' }} onClick={() => handleDeleteUser(user.id)} disabled={submitting}>Ja</Button>
                            <Button variant="outline-secondary" size="sm" style={{ borderRadius: '8px', padding: '4px 12px' }} onClick={() => setDeleteConfirmId(null)} disabled={submitting}>Nein</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="d-flex gap-2 w-100">
                          <Button variant="outline-primary" size="sm" className="w-50" style={{ borderRadius: '8px', borderColor: '#b95c50', color: '#b95c50' }} onClick={() => startEdit(user)} disabled={submitting || editUserId !== null}>PW ändern</Button>
                          <Button variant="outline-danger" size="sm" className="w-50" style={{ borderRadius: '8px' }} onClick={() => { setDeleteConfirmId(user.id); cancelEdit(); }} disabled={submitting || editUserId !== null}>Löschen</Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
